import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Brain, Zap, Sparkles, TrendingUp, Eye, MessageSquare, BarChart3, Cpu, ArrowRight, CheckCircle } from 'lucide-react';

const AISection: React.FC = () => {
  const navigate = useNavigate();
  const [showComingSoon, setShowComingSoon] = useState(false);

  const handleComingSoon = () => {
    setShowComingSoon(true);
    window.setTimeout(() => setShowComingSoon(false), 3000);
  };

  const aiServices = [
    {
      icon: MessageSquare,
      title: 'Chatbots Intelligents',
      description: 'Assistants virtuels conversationnels qui comprennent et répondent naturellement à vos clients 24/7.',
      features: ['Traitement du langage naturel', 'Apprentissage continu', 'Intégration multicanal'],
      color: 'from-amber-500 to-amber-600'
    },
    {
      icon: Eye,
      title: 'Vision par Ordinateur',
      description: 'Analyse et reconnaissance d\'images pour automatiser vos processus visuels et contrôles qualité.',
      features: ['Reconnaissance d\'objets', 'Détection d\'anomalies', 'Classification automatique'],
      color: 'from-amber-400 to-amber-500'
    },
    {
      icon: BarChart3,
      title: 'Analyse Prédictive',
      description: 'Algorithmes d\'apprentissage automatique pour anticiper les tendances et optimiser vos décisions.',
      features: ['Prévisions de ventes', 'Détection de fraudes', 'Optimisation des stocks'],
      color: 'from-amber-500 to-orange-500'
    },
    {
      icon: Cpu,
      title: 'Automatisation Intelligente',
      description: 'Robots logiciels qui automatisent vos tâches répétitives avec une intelligence adaptative.',
      features: ['RPA intelligent', 'Workflows adaptatifs', 'Prise de décision autonome'],
      color: 'from-amber-600 to-orange-600'
    }
  ];

  const stats = [
    { number: '85%', label: 'Réduction des coûts opérationnels' },
    { number: '300%', label: 'Amélioration de la productivité' },
    { number: '24/7', label: 'Disponibilité continue' },
    { number: '99.9%', label: 'Précision des analyses' }
  ];

  return (
    <section className="section-padding bg-gradient-to-br from-blue-950 via-blue-900 to-slate-900 text-white relative overflow-hidden">
      {/* Effets de fond */}
      <div className="absolute inset-0">
        <div className="absolute top-20 left-10 w-72 h-72 bg-amber-500/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-orange-500/10 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-to-r from-amber-500/5 to-orange-500/5 rounded-full blur-3xl"></div>
      </div>

      <div className="container-max relative z-10">
        {/* Header */}
        <div className="text-center mb-20">
          <div className="flex items-center justify-center space-x-3 mb-6">
            <Brain className="w-12 h-12 text-amber-400" />
            <Sparkles className="w-8 h-8 text-amber-500 animate-pulse" />
            <Zap className="w-10 h-10 text-amber-400" />
          </div>
          
          <h2 className="text-5xl md:text-6xl font-bold mb-6">
            <span className="bg-gradient-to-r from-amber-300 via-amber-400 to-amber-500 bg-clip-text text-transparent">
              Intelligence Artificielle
            </span>
          </h2>
          
          <p className="text-xl text-gray-300 max-w-4xl mx-auto leading-relaxed mb-8">
            L'IA n'est plus l'avenir, c'est le présent. Nous créons des solutions d'intelligence artificielle 
            qui transforment radicalement votre façon de travailler et d'interagir avec vos clients.
          </p>

          <div className="inline-flex items-center space-x-2 bg-gradient-to-r from-amber-500 to-amber-600 px-6 py-3 rounded-full text-white font-medium shadow-lg">
            <TrendingUp className="w-5 h-5" />
            <span>Le futur de la technologie</span>
          </div>
        </div>

        {/* Services IA */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-20">
          {aiServices.map((service, index) => (
            <div
              key={index}
              className="group"
            >
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8 hover:bg-white/10 transition-all duration-500 hover:-translate-y-2">
                <div className="flex items-center mb-6">
                  <div className={`w-16 h-16 bg-gradient-to-r ${service.color} rounded-2xl flex items-center justify-center mr-4 group-hover:scale-110 transition-transform duration-300`}>
                    <service.icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-white group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-amber-300 group-hover:to-amber-500 transition-all duration-300">
                    {service.title}
                  </h3>
                </div>
                
                <p className="text-gray-300 mb-6 leading-relaxed">
                  {service.description}
                </p>

                <div className="space-y-3">
                  {service.features.map((feature, idx) => (
                    <div key={idx} className="flex items-center text-sm text-gray-400">
                      <CheckCircle className="w-4 h-4 text-amber-400 mr-3 flex-shrink-0" />
                      <span>{feature}</span>
                    </div>
                  ))}
                </div>

                <div className="mt-6">
                  <button 
                    onClick={handleComingSoon}
                    className={`bg-gradient-to-r ${service.color} text-white px-6 py-3 rounded-xl font-medium hover:shadow-lg transition-all duration-300 flex items-center space-x-2 group-hover:scale-105`}
                  >
                    <span>Explorer cette solution</span>
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Statistiques */}
        <div className="bg-gradient-to-r from-blue-900/40 to-blue-800/30 backdrop-blur-sm border border-amber-400/20 rounded-2xl p-8 mb-16">
          <h3 className="text-3xl font-bold text-center text-white mb-12">
            L'Impact de l'IA sur votre Business
          </h3>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div
                key={index}
                className="text-center"
              >
                <div className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-amber-500 mb-2">
                  {stat.number}
                </div>
                <div className="text-gray-300 text-sm">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Vision du futur */}
        <div className="text-center">
          <div className="bg-gradient-to-r from-blue-900/30 to-blue-800/20 backdrop-blur-sm border border-amber-400/20 rounded-2xl p-12">
            <h3 className="text-4xl font-bold text-white mb-6">
              L'IA : Le Moteur de l'Innovation de Demain
            </h3>
            
            <p className="text-xl text-gray-300 max-w-4xl mx-auto leading-relaxed mb-8">
              Nous ne nous contentons pas de suivre les tendances technologiques, nous les créons. 
              L'intelligence artificielle transforme déjà tous les secteurs d'activité. 
              <strong className="text-white"> Ne restez pas spectateur de cette révolution, devenez-en l'acteur principal.</strong>
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <div className="bg-white/5 rounded-xl p-6">
                <Brain className="w-12 h-12 text-amber-400 mx-auto mb-4" />
                <h4 className="text-lg font-semibold text-white mb-2">Intelligence Augmentée</h4>
                <p className="text-gray-400 text-sm">Amplifiez les capacités humaines avec l'IA</p>
              </div>
              
              <div className="bg-white/5 rounded-xl p-6">
                <Zap className="w-12 h-12 text-amber-400 mx-auto mb-4" />
                <h4 className="text-lg font-semibold text-white mb-2">Automatisation Totale</h4>
                <p className="text-gray-400 text-sm">Libérez votre potentiel créatif</p>
              </div>
              
              <div className="bg-white/5 rounded-xl p-6">
                <TrendingUp className="w-12 h-12 text-amber-400 mx-auto mb-4" />
                <h4 className="text-lg font-semibold text-white mb-2">Croissance Exponentielle</h4>
                <p className="text-gray-400 text-sm">Dépassez vos limites actuelles</p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button 
                onClick={handleComingSoon}
                className="bg-gradient-to-r from-amber-500 to-amber-600 text-white px-8 py-4 rounded-xl font-bold hover:shadow-2xl hover:shadow-amber-500/30 transition-all duration-300 flex items-center justify-center space-x-2 hover:scale-105"
              >
                <Brain className="w-5 h-5" />
                <span>Découvrir nos solutions IA</span>
              </button>
              
              <button 
                onClick={() => navigate('/devisrequest')}
                className="border border-amber-400/30 text-white px-8 py-4 rounded-xl font-medium hover:bg-amber-500/10 hover:border-amber-400/50 transition-all duration-300 flex items-center justify-center space-x-2"
              >
                <span>Planifier une consultation</span>
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>

            {showComingSoon && (
              <p className="mt-6 text-amber-300 font-semibold text-lg animate-pulse">
                Bientôt disponible
              </p>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default AISection;
